import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { useToast } from 'src/shared/hooks/use-toast'
import { Database } from 'supabase/database.types'

import { queryKeys } from './queryKeys'

export type InsertProductVariant =
  Database['public']['Tables']['product_variant']['Insert']
export type ProductVariant_ =
  Database['public']['Tables']['product_variant']['Row'] & {
    product_variant_color: Database['public']['Tables']['product_variant_color']['Row'] & {
      product_image: Database['public']['Tables']['product_image']['Row'][]
    }
  }

export const useProductVariantApi = ({
  product_id,
}: {
  product_id?: number
}) => {
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()
  const { toast } = useToast()

  const { data: productVariants } = useQuery({
    queryKey: [queryKeys.productVariants],
    queryFn: async () =>
      await supabase
        .from('product_variant')
        .select('*, product_variant_color(*, product_image(*))')
        .eq('product_id', product_id!),
    enabled: !!product_id,
  })

  const createProductVariant = useMutation({
    mutationFn: async (insertProductVariant: InsertProductVariant) =>
      await supabase.from('product_variant').insert(insertProductVariant),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.productVariants] })
    },
  })

  const updateProductVariant = useMutation({
    mutationFn: async (insertProductVariant: InsertProductVariant) =>
      await supabase
        .from('product_variant')
        .update(insertProductVariant)
        .eq('id', insertProductVariant.id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.productVariants] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.packageProducts] })

      toast({
        title: 'Variant updated',
        description: 'Variant updated successfully',
      })
    },
  })

  const deleteProductVariant = useMutation({
    mutationFn: async (id: number) =>
      await supabase.from('product_variant').delete().eq('id', id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.productVariants] })
    },
  })

  return {
    productVariants,
    createProductVariant,
    updateProductVariant,
    deleteProductVariant,
  }
}
